import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";

/**
 * Dark-themed multi-select with chips. Designed for onboarding so the
 * Hedef Müşteri Tipi field stops being free-text and the analysis engine
 * gets cleaner, structured signals.
 */
export default function MultiSelect({ options, value = [], onChange, placeholder = "Seçiniz", testid, labelFor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const display = (v) => (typeof labelFor === "function" ? labelFor(v) : v);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };
  const remove = (opt) => onChange(value.filter((v) => v !== opt));

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full min-h-[42px] flex flex-wrap gap-1.5 items-center rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-left hover:border-white/20 focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_1px_#3b82f6]"
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        {value.length === 0 ? (
          <span className="text-slate-500 text-sm">{placeholder}</span>
        ) : (
          value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 bg-cyan-500/10 border border-cyan-500/20 text-cyan-200 text-xs rounded-full px-2 py-0.5"
            >
              {display(v)}
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); remove(v); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); remove(v); } }}
                className="text-cyan-300 hover:text-white cursor-pointer"
                aria-label={`Remove ${display(v)}`}
              >
                <X size={11} />
              </span>
            </span>
          ))
        )}
        <ChevronDown size={14} className="ml-auto text-slate-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-lg bg-[#0b0b14] border border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.6)] p-1" data-testid={testid ? `${testid}-menu` : undefined}>
          {options.map((opt) => {
            const checked = value.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-left ${checked ? "bg-cyan-500/10 text-white" : "text-slate-300 hover:bg-white/[0.04]"}`}
                data-testid={testid ? `${testid}-opt-${opt.replace(/\s+/g, "-").toLowerCase()}` : undefined}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? "bg-cyan-500 border-cyan-500" : "border-white/20"}`}>
                  {checked && <Check size={11} className="text-white" />}
                </span>
                {display(opt)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
